import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import { FulfillmentFlatTeamSettingGroupById } from "./groups";

export type FulfillmentFlatTeamSettingKey =
  | "outboundLabels/defaultShipFromLocationExternalId"
  | "outboundLabels/address/internationalAddressOutreach"
  | "outboundLabels/documents/packingSlip/perParcelSlips"
  | "outboundLabels/disableKeyboardOnScan"
  | "outboundLabels/scanVerify/manualQuantityEntry";

const shipFromLocationKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/defaultShipFromLocationExternalId";
const shipFromLocationGroupId = "fulfillment.ship_from_locations" as const;

const internationalAddressOutreachKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/address/internationalAddressOutreach";
const addressGroupId = "fulfillment.address" as const;

const perParcelSlipsKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/documents/packingSlip/perParcelSlips";
const printingGroupId = "fulfillment.printing" as const;

const manualQuantityEntryKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/scanVerify/manualQuantityEntry";
const disableKeyboardOnScanKey: FulfillmentFlatTeamSettingKey =
  "outboundLabels/disableKeyboardOnScan";
const scanToVerifyGroupId = "fulfillment.scan_to_verify" as const;

export const FulfillmentFlatTeamSettingCatalogDefinitions = [
  {
    id: shipFromLocationKey,
    groupId: shipFromLocationGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Default ship from location",
        description:
          "Selects the ship-from location used automatically when creating a manual order.",
        synonyms: [
          "default ship from location",
          "default warehouse",
          "manual order location",
          "shipping origin default",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[shipFromLocationGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: shipFromLocationKey },
  },
  {
    id: disableKeyboardOnScanKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Disable manual entry in the scan barcode field",
        description:
          "Prevents non-admin operators from typing or pasting into the scan barcode field while continuing to accept hardware barcode scans. Separately enabled manual quantity entry remains available.",
        synonyms: [
          "disable manual barcode entry",
          "barcode scanner only",
          "prevent typing barcode",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: disableKeyboardOnScanKey },
  },
  {
    id: manualQuantityEntryKey,
    groupId: scanToVerifyGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label:
          "Allow users to manually enter item quantity when line item quantity is greater than",
        description:
          "Lets operators type the verified quantity for larger line items instead of scanning every unit. The threshold determines which line items show an editable quantity input.",
        synonyms: [
          "manual scan quantity",
          "scan to verify quantity",
          "enter verified item quantity",
          "bulk item verification",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[scanToVerifyGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: manualQuantityEntryKey },
  },
  {
    id: internationalAddressOutreachKey,
    groupId: addressGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Include international orders",
        description:
          "Applies address verification failure handling to orders shipping outside the US. When on, international orders get the same correction emails and holds as domestic ones; when off, Redo takes no action on an international address it could not verify. Off by default because international addresses verify less reliably, so turning it on increases both emails and holds.",
        synonyms: [
          "international address verification",
          "international address emails",
          "non-US addresses",
          "overseas orders address",
          "address outreach international",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[addressGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: {
      adapter: "teamSettings",
      key: internationalAddressOutreachKey,
    },
  },
  {
    id: perParcelSlipsKey,
    groupId: printingGroupId,
    metadata: createSettingMetadataFromGroupDefaults({
      setting: {
        label: "Print a packing slip per parcel",
        description:
          "When an order ships in more than one box, prints a separate packing slip for each box listing only the items allocated to it, and names that box in the slip's footer. Off by default: with it off, a multi-box order gets one slip listing every item in the order.",
        synonyms: [
          "packing slip per parcel",
          "packing slip per box",
          "one slip per box",
          "slip for each package",
          "multi parcel packing slips",
          "split packing slip by box",
        ],
      },
      group: FulfillmentFlatTeamSettingGroupById[printingGroupId],
      resolveAgentEditability: ({ group }) => group.agentEditability,
    }),
    valueSource: { adapter: "teamSettings", key: perParcelSlipsKey },
  },
] satisfies readonly SettingDefinition<
  FulfillmentFlatTeamSettingKey,
  | typeof shipFromLocationGroupId
  | typeof addressGroupId
  | typeof printingGroupId
  | typeof scanToVerifyGroupId,
  TeamSettingsSettingValueSource<FulfillmentFlatTeamSettingKey>
>[];

export const FulfillmentFlatTeamSettingMetadataByKey = Object.fromEntries(
  FulfillmentFlatTeamSettingCatalogDefinitions.map((definition) => [
    definition.id,
    definition.metadata,
  ]),
) as Record<FulfillmentFlatTeamSettingKey, SettingMetadata>;
